import * as Draft from 'draft-js';

import { generateHash } from 'utils/generateHash';
import { getDecimalSeparator } from 'utils/number/locale';
import { stringAddStartIfNotStartsWith } from 'utils/stringAddStartIfNotStartsWith';
// import { stringRemoveStartIfStartsWith } from 'utils/stringRemoveStartIfStartsWith';
import { stringUnwrap } from 'utils/stringUnwrap';
import { stringWrap } from 'utils/stringWrap';

import { EditorEntityKind } from './editorEntityKind';

// На бэкенде формулы начинаются с символа '='
export const API_FORMULA_START = '=';

// На бэкенд все символы доллара отправляем как #DOLLAR#,
// чтобы не спутать со ссылкой на id формата $1234
const DOLLAR_ESCAPED = '#DOLLAR#';

// На фронте символы доллара показываем как обычный $
const DOLLAR_FRONT = '$';

// Чтобы распарсить формулу, сначала подменяем экранированные доллары
const PRE_PARSING_REPLACEMENT = '@DOLLAR@';

const PRE_PARSING_REPLACEMENT_REGEX = /@DOLLAR@([^@]+)@DOLLAR@/g;

// Паттерн определяет, что это ссылка на колонку таблицы ($1234) или $1234.
// Проверяет, что id может быть завернуто в скобочки для дополнительного экранирования:
// Пример: ($12345)123 — это id и простое число. Без скобок не получится разделить id и число
const API_FILED_ID_REGEX = /(\(\$[0-9]+\)|\$[0-9]+)/g;

// Паттерн на Id поля в скобках ($1234)
const API_FILED_ID_IN_BRACKETS_REGEX = /^\(\$[0-9]+\)$/;

// Id поля в серверном формате.
// Дополнительно оборачиваем в скобочки, чтобы ID не склеивалось с обычными числами справа
const escapeFieldIdForId = (fieldId: string): string => `($${fieldId})`;

/**
 * Проверяем, что это строка формата: '$1234', т.е. id колонки таблицы
 */
const isFormFieldIdApi = (tokenEntityApi: string): boolean => {
  const matched = tokenEntityApi.match(API_FILED_ID_REGEX);

  return (
    Array.isArray(matched) &&
    matched.length === 1 &&
    matched[0] === tokenEntityApi
  );
};

export const normalizeToServerString = (
  rawDraftState: Draft.RawDraftContentState
): string => {
  let result = rawDraftState.blocks[0].text;

  // Бежим по всем Draft.js-сущностям и вставляем в финальную строку
  // вместо лейбла ID соответствующих колонок таблицы

  // В процессе замены лейблов на ID, меняется длина строки.
  // В currentShift записываем смещение координаты подстановки
  let currentShift = 0;

  rawDraftState.blocks[0].entityRanges.forEach(({ offset, length, key }) => {
    if (rawDraftState.entityMap[key].type !== EditorEntityKind.formField) {
      return;
    }

    // Подстрока ДО лейбла, который надо заменить на ID
    const before = result.substring(0, offset + currentShift);

    // Подстрока ПОСЛЕ лейбла, который надо заменить на ID
    const after = result.substring(offset + currentShift + length);

    // Оборачиваем id строкой @DOLLAR@ слева и справа.
    // Пример: ТН Руководителя -> @DOLLAR@1234@DOLLAR@
    //   затем заменяем все доллары, которые пользователь впечатал сам на экранированные #DOLLAR#.
    const replaced = stringWrap(
      PRE_PARSING_REPLACEMENT,
      rawDraftState.entityMap[key].data.fieldId
    );

    result = [before, replaced, after].join('');
    currentShift += replaced.length - length;
  });

  // Все доллары, которые пользователь ввел руками, заменяем на #DOLLAR# для бэкенда
  result = result.replaceAll(DOLLAR_FRONT, DOLLAR_ESCAPED);

  // Теперь все ID сущностей экранируем в формат бэкенда
  // @DOLLAR@1234@DOLLAR@ -> ($1234)
  result = result.replaceAll(PRE_PARSING_REPLACEMENT_REGEX, (_, matchedId) =>
    escapeFieldIdForId(matchedId)
  );

  // Нормализуем разделители для серверного формата
  if (getDecimalSeparator() === ',') {
    // Русская локаль: сначала десятичные запятые → точки, потом ; → ,
    // 1) Десятичные запятые (между цифрами) → точки
    result = result.replace(/(\d),(\d)/g, '$1.$2');
    // 2) Точки с запятой (аргументы) → запятые
    result = result.replace(/;/g, ',');
  }

  // Добавляем «=» в начале строки
  return stringAddStartIfNotStartsWith(API_FORMULA_START, result);
};

export type NormalizeToRawDraftStateParams = {
  serverString: string;
  getLabelById: (draftEntityKey: string) => string;
  onRemoveEntity: (id: string) => void;
};

export const normalizeFromServerToRawDraftState = ({
  serverString,
  getLabelById,
  onRemoveEntity,
}: NormalizeToRawDraftStateParams): Draft.RawDraftContentState => {
  // Добавляем лидирующий «=»
  const formulaEntityPayload = stringAddStartIfNotStartsWith(
    API_FORMULA_START,
    serverString
  );

  // Создаем начальное состояние редактора
  const rawDraftState: Draft.RawDraftContentState = {
    // Блоки это типа абзацы. Поскольку формула — это один абзац, то и блок один
    blocks: [
      {
        key: generateHash(),
        text: '',
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
    entityMap: {},
  };

  // Какую по счету сущность распарсили
  let currentEntityIndex = 0;

  // Строка, которую финально передадим в редактор
  let currentContentString = '';

  formulaEntityPayload
    // Разбиваем строку по ID
    .split(API_FILED_ID_REGEX)
    // Если это переменна в формате ($123) удаляем лишние скобки -> $123
    .map((chunk) => {
      if (chunk.match(API_FILED_ID_IN_BRACKETS_REGEX)) {
        return stringUnwrap('(', chunk, ')');
      }

      return chunk;
    })
    // Убираем пустые строки
    .filter((chunk) => chunk.length > 0)
    // Нормализуем каждый токен к фронтовой сущности
    .forEach((token) => {
      if (isFormFieldIdApi(token)) {
        // $1324 -> 1234
        const fieldId = token.substring(1);

        const fieldLabel = getLabelById(fieldId);

        rawDraftState.entityMap[currentEntityIndex] = {
          mutability: 'IMMUTABLE',
          type: EditorEntityKind.formField,
          data: {
            fieldId,
            onRemoveEntity,
          },
        };

        // Указываем левую и правую координату лейбла поля для редактора
        rawDraftState.blocks[0].entityRanges.push({
          length: fieldLabel.length,
          offset: currentContentString.length,
          key: currentEntityIndex,
        });

        currentEntityIndex++;
        currentContentString += fieldLabel;

        return;
      }

      // Если это обычный текст, то в чанке заменяем #DOLLAR# на $ и прибавляем к результату
      // Это нужно делать для каждого чанка по отдельности, чтобы на момент парсинга очередного чанка,
      // длина контента слева уже не менялась
      currentContentString += token.replaceAll(DOLLAR_ESCAPED, DOLLAR_FRONT);
    });

  rawDraftState.blocks[0].text = currentContentString;

  // Денормализуем разделители для отображения в редакторе
  const decimalSep = getDecimalSeparator();
  if (decimalSep === ',') {
    let text = rawDraftState.blocks[0].text;
    // На сервере `,` — ВСЕГДА аргументный разделитель, `.` — десятичная точка
    // 1) Запятые-аргументы → точки с запятой
    text = text.replace(/,/g, ';');
    // 2) Десятичные точки → запятые (точка между цифрами)
    text = text.replace(/(\d)\.(\d)/g, '$1,$2');
    rawDraftState.blocks[0].text = text;
  }

  return rawDraftState;
};
